import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { EXPOSURE_META, fmtDate } from "@/lib/exposure";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "users",       label: "Users" },
  { id: "assessments", label: "Assessments" },
  { id: "audits",      label: "Audits" },
  { id: "names",       label: "Name Tests" },
  { id: "chats",       label: "Chats" },
];

const renderInline = (text) => {
  if (!text) return null;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  let k = 0;
  while ((m = linkRegex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <a key={`l-${k++}`} href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: "var(--cobalt)" }}>
        {m[1]}
      </a>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
};

export const Admin = () => {
  const [params, setParams] = useSearchParams();
  const [tokenInput, setTokenInput] = useState(params.get("token") || "");
  const token = params.get("token") || "";
  const activeTab = params.get("tab") || "users";

  const setTab = (id) => {
    const p = new URLSearchParams(params);
    p.set("tab", id);
    setParams(p);
  };

  const submitToken = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    const p = new URLSearchParams(params);
    p.set("token", tokenInput.trim());
    if (!p.get("tab")) p.set("tab", "users");
    setParams(p);
  };

  return (
    <div data-testid="admin-page">
      <header className="brand-navy-band">
        <div className="container-wide" style={{ padding: "0.9rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/dashboard" data-testid="admin-back-to-app" style={{ color: "var(--warm-white)", textDecoration: "none", display: "inline-flex", alignItems: "baseline", gap: "0.7rem", flexWrap: "wrap" }}>
            <span
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 800,
                fontSize: 22,
                letterSpacing: "-0.01em",
                color: "var(--warm-white)",
                lineHeight: 1,
              }}
            >
              Legally<span style={{ color: "var(--yellow)" }}>Legit</span>.ai
            </span>
            <span aria-hidden="true" style={{ display: "inline-block", width: 6, height: 6, background: "var(--yellow)", alignSelf: "center" }} />
            <span style={{ fontWeight: 700, letterSpacing: "0.1em", fontSize: 11, textTransform: "uppercase", color: "rgba(242,233,219,0.85)" }}>
              Brand Protection Toolkit · Admin
            </span>
          </Link>
          <Link to="/dashboard" style={{ color: "var(--warm-white)", textDecoration: "none", fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", borderBottom: "2px solid var(--yellow)", paddingBottom: 2 }}>
            Back to App →
          </Link>
        </div>
      </header>

      {!token && (
        <section style={{ padding: "3rem 0 4rem" }}>
          <div className="container-narrow callout">
            <p className="eyebrow">Internal · Joey-only</p>
            <h1 style={{ margin: "0.3rem 0 1.1rem" }}>Paste your admin token</h1>
            <form onSubmit={submitToken} style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }} data-testid="admin-token-form">
              <input
                type="password"
                placeholder="X-Admin-Token"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                className="brand-input"
                data-testid="admin-token-input"
                style={{ flex: 1, minWidth: 240 }}
              />
              <button type="submit" className="btn-yellow" data-testid="admin-token-submit">
                Unlock →
              </button>
            </form>
            <p style={{ margin: "0.9rem 0 0", fontSize: "0.85rem", color: "var(--midnight)" }}>
              Bookmark <code>?token=YOUR_TOKEN</code> for instant re-entry.
            </p>
          </div>
        </section>
      )}

      {token && (
        <>
          <nav style={{ borderBottom: "1px solid var(--hairline)", background: "var(--warm-white-soft)" }} data-testid="admin-tabs">
            <div className="container-wide" style={{ display: "flex", gap: "0.2rem", flexWrap: "wrap" }}>
              {TABS.map((t) => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    data-testid={`admin-tab-${t.id}`}
                    style={{
                      background: "transparent",
                      border: "none",
                      borderBottom: isActive ? "3px solid var(--navy)" : "3px solid transparent",
                      color: isActive ? "var(--navy)" : "var(--cobalt)",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      fontSize: "0.82rem",
                      padding: "1rem 1.1rem",
                      cursor: "pointer",
                      fontFamily: "Arial, sans-serif",
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </nav>

          <section style={{ padding: "2rem 0 4rem" }}>
            <div className="container-wide">
              {activeTab === "users"       && <UsersTab token={token} />}
              {activeTab === "assessments" && <AssessmentsTab token={token} />}
              {activeTab === "audits"      && <AuditsTab token={token} />}
              {activeTab === "names"       && <NameTestsTab token={token} />}
              {activeTab === "chats"       && <ChatsTab token={token} />}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Reusable hook: fetch a resource with the admin token
// ────────────────────────────────────────────────────────────

const useAdminFetch = (path, token) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}${path}`, { headers: { "X-Admin-Token": token } });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 401 ? "Invalid admin token." :
          err?.response?.data?.detail || "Couldn't load data."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [path, token, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  return { data, error, loading, refresh };
};

// ────────────────────────────────────────────────────────────
// Shared UI bits
// ────────────────────────────────────────────────────────────

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} style={{ background: "var(--paper)", border: "1px solid var(--hairline)", padding: "0.7rem 1rem", borderRadius: 3, minWidth: 140 }}>
    <div className="eyebrow" style={{ margin: 0 }}>{label}</div>
    <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--navy)", lineHeight: 1.1, marginTop: 4 }}>{value}</div>
  </div>
);

const FilterBar = ({ filter, setFilter, testId, children }) => (
  <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1.25rem" }}>
    {children}
    <input
      type="text"
      placeholder="Filter by name, email, or content..."
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
      className="brand-input"
      data-testid={testId}
      style={{ flex: 1, minWidth: 260, maxWidth: 480 }}
    />
  </div>
);

const ExposurePill = ({ level }) => {
  if (!level) return null;
  const meta = EXPOSURE_META[level] || EXPOSURE_META.medium;
  return (
    <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "0.2rem 0.5rem", background: meta.color, color: "var(--warm-white)", letterSpacing: "0.05em" }}>
      {meta.label}
    </span>
  );
};

const Row = ({ children, testId }) => (
  <article className="card-paper" data-testid={testId} style={{ padding: "1.1rem 1.3rem", marginBottom: "0.85rem" }}>
    {children}
  </article>
);

const Header = ({ left, right }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", alignItems: "baseline" }}>
    <div>{left}</div>
    {right && <div>{right}</div>}
  </div>
);

const Identity = ({ first_name, email }) => (
  <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: "1.02rem" }}>
    {first_name} <span style={{ color: "var(--cobalt)", fontWeight: 400, fontSize: "0.92rem" }}>· {email}</span>
  </div>
);

// ────────────────────────────────────────────────────────────
// USERS TAB
// ────────────────────────────────────────────────────────────

const UsersTab = ({ token }) => {
  const { data, error, loading, refresh } = useAdminFetch("/admin/users", token);
  const [filter, setFilter] = useState("");
  const users = data?.users || [];

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return users;
    return users.filter((u) => `${u.first_name} ${u.email}`.toLowerCase().includes(f));
  }, [users, filter]);

  const activeCount = users.filter((u) => u.status === "active").length;

  const resetPassword = async (email) => {
    const np = window.prompt(`Set new password for ${email}\n(8+ characters)`);
    if (!np || np.length < 8) {
      if (np !== null) alert("Password must be at least 8 characters.");
      return;
    }
    try {
      await axios.post(`${API}/admin/set-user-password`, { email, new_password: np }, { headers: { "X-Admin-Token": token } });
      alert(`Password updated for ${email}.`);
      refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to reset password.");
    }
  };

  const removeUser = async (email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, { headers: { "X-Admin-Token": token } });
      refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete user.");
    }
  };

  return (
    <div data-testid="admin-users-tab">
      <FilterBar filter={filter} setFilter={setFilter} testId="admin-users-filter">
        <Stat label="Total users" value={users.length} testId="admin-users-stat-total" />
        <Stat label="Active" value={activeCount} testId="admin-users-stat-active" />
      </FilterBar>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {!loading && !error && visible.length === 0 && (
        <p style={{ color: "var(--cobalt)" }}>No users match.</p>
      )}

      {visible.map((u) => (
        <Row key={u.id} testId={`admin-user-${u.email}`}>
          <Header
            left={
              <>
                <Identity first_name={u.first_name} email={u.email} />
                <div style={{ fontSize: "0.82rem", color: "var(--midnight)", marginTop: 4, opacity: 0.75 }}>
                  Status: <strong>{u.status}</strong>
                  {u.source ? ` · Source: ${u.source}` : ""}
                  {u.login_count ? ` · Logins: ${u.login_count}` : ""}
                  {u.last_login_at ? ` · Last login: ${fmtDateTime(u.last_login_at)}` : ""}
                  {` · Signed up: ${fmtDateTime(u.created_at)}`}
                </div>
              </>
            }
            right={
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button type="button" onClick={() => resetPassword(u.email)} className="btn-outline-navy" data-testid={`admin-user-reset-${u.email}`} style={{ padding: "0.4rem 0.7rem", fontSize: "0.8rem" }}>Reset password</button>
                <button type="button" onClick={() => removeUser(u.email)} data-testid={`admin-user-delete-${u.email}`} style={{ padding: "0.4rem 0.7rem", fontSize: "0.8rem", border: "2px solid #b91c1c", color: "#b91c1c", background: "transparent", fontWeight: 700, cursor: "pointer" }}>Delete</button>
              </div>
            }
          />
        </Row>
      ))}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// ASSESSMENTS TAB
// ────────────────────────────────────────────────────────────

const AssessmentsTab = ({ token }) => {
  const { data, error, loading } = useAdminFetch("/admin/assessments", token);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState({});
  const items = data?.assessments || [];

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return items;
    return items.filter((a) =>
      `${a.first_name} ${a.email} ${a.headline || ""} ${a.protect_first_asset || ""}`.toLowerCase().includes(f)
    );
  }, [items, filter]);

  return (
    <div data-testid="admin-assessments-tab">
      <FilterBar filter={filter} setFilter={setFilter} testId="admin-assessments-filter">
        <Stat label="Total runs" value={items.length} testId="admin-assessments-stat-total" />
      </FilterBar>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {!loading && !error && visible.length === 0 && (
        <p style={{ color: "var(--cobalt)" }}>No assessments yet.</p>
      )}

      {visible.map((a) => {
        const isOpen = !!open[a.id];
        return (
          <Row key={a.id} testId={`admin-assessment-${a.id}`}>
            <Header
              left={
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <ExposurePill level={a.exposure_level} />
                    <Identity first_name={a.first_name} email={a.email} />
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "var(--midnight)", marginTop: 4, opacity: 0.75 }}>
                    {fmtDateTime(a.created_at)}{a.protect_first_asset ? ` · Protect first: ${a.protect_first_asset}` : ""}
                  </div>
                  {a.headline && (
                    <div style={{ marginTop: "0.55rem", color: "var(--midnight)", fontSize: "0.95rem", lineHeight: 1.5 }}>
                      {a.headline}
                    </div>
                  )}
                </>
              }
              right={
                <button type="button" onClick={() => setOpen((s) => ({ ...s, [a.id]: !s[a.id] }))} className="btn-outline-navy" data-testid={`admin-assessment-toggle-${a.id}`} style={{ padding: "0.4rem 0.7rem", fontSize: "0.8rem" }}>
                  {isOpen ? "Hide ▲" : "View ▼"}
                </button>
              }
            />
            {isOpen && (
              <div style={{ marginTop: "1rem", borderTop: "1px solid var(--hairline)", paddingTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>Their answers</div>
                  <KV pairs={a.answers} />
                </div>
                <div>
                  <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>Priority Map</div>
                  <PrettyJson data={a.report} />
                </div>
              </div>
            )}
          </Row>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// AUDITS TAB
// ────────────────────────────────────────────────────────────

const AuditsTab = ({ token }) => {
  const { data, error, loading } = useAdminFetch("/admin/audits", token);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState({});
  const items = data?.audits || [];

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return items;
    return items.filter((a) =>
      `${a.first_name} ${a.email} ${a.brand_name || ""} ${a.headline || ""}`.toLowerCase().includes(f)
    );
  }, [items, filter]);

  return (
    <div data-testid="admin-audits-tab">
      <FilterBar filter={filter} setFilter={setFilter} testId="admin-audits-filter">
        <Stat label="Total audits" value={items.length} testId="admin-audits-stat-total" />
      </FilterBar>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {!loading && !error && visible.length === 0 && (
        <p style={{ color: "var(--cobalt)" }}>No audits yet.</p>
      )}

      {visible.map((a) => {
        const isOpen = !!open[a.id];
        return (
          <Row key={a.id} testId={`admin-audit-${a.id}`}>
            <Header
              left={
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <ExposurePill level={a.exposure_level} />
                    <Identity first_name={a.first_name} email={a.email} />
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "var(--midnight)", marginTop: 4, opacity: 0.75 }}>
                    {fmtDateTime(a.created_at)}{a.brand_name ? ` · Brand: ${a.brand_name}` : ""}
                  </div>
                  {a.headline && (
                    <div style={{ marginTop: "0.55rem", color: "var(--midnight)", fontSize: "0.95rem", lineHeight: 1.5 }}>
                      {a.headline}
                    </div>
                  )}
                  {a.biggest_worry && (
                    <div style={{ marginTop: "0.4rem", color: "var(--cobalt)", fontSize: "0.88rem", fontStyle: "italic" }}>
                      Worry: "{a.biggest_worry}"
                    </div>
                  )}
                </>
              }
              right={
                <button type="button" onClick={() => setOpen((s) => ({ ...s, [a.id]: !s[a.id] }))} className="btn-outline-navy" data-testid={`admin-audit-toggle-${a.id}`} style={{ padding: "0.4rem 0.7rem", fontSize: "0.8rem" }}>
                  {isOpen ? "Hide ▲" : "View ▼"}
                </button>
              }
            />
            {isOpen && (
              <div style={{ marginTop: "1rem", borderTop: "1px solid var(--hairline)", paddingTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>Their answers</div>
                  <KV pairs={a.answers} />
                </div>
                <div>
                  <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>Action Plan</div>
                  <PrettyJson data={a.report} />
                </div>
              </div>
            )}
          </Row>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// NAME TESTS TAB
// ────────────────────────────────────────────────────────────

const NameTestsTab = ({ token }) => {
  const { data, error, loading } = useAdminFetch("/admin/name-tests", token);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState({});
  const items = data?.name_tests || [];

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return items;
    return items.filter((n) =>
      `${n.first_name} ${n.email} ${n.name || ""} ${n.headline || ""}`.toLowerCase().includes(f)
    );
  }, [items, filter]);

  return (
    <div data-testid="admin-names-tab">
      <FilterBar filter={filter} setFilter={setFilter} testId="admin-names-filter">
        <Stat label="Names evaluated" value={items.length} testId="admin-names-stat-total" />
      </FilterBar>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {!loading && !error && visible.length === 0 && (
        <p style={{ color: "var(--cobalt)" }}>No name tests yet.</p>
      )}

      {visible.map((n) => {
        const isOpen = !!open[n.id];
        const score = typeof n.overall_score === "number" ? n.overall_score : null;
        const scoreColor =
          score == null   ? "var(--cobalt)" :
          score >= 75     ? "var(--evergreen)" :
          score >= 50     ? "#ea580c" :
          "#b91c1c";
        return (
          <Row key={n.id} testId={`admin-name-${n.id}`}>
            <Header
              left={
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    {score != null && (
                      <span style={{ fontSize: "0.78rem", fontWeight: 700, padding: "0.2rem 0.55rem", background: scoreColor, color: "var(--warm-white)", letterSpacing: "0.04em" }}>
                        {score}/100
                      </span>
                    )}
                    <Identity first_name={n.first_name} email={n.email} />
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "var(--midnight)", marginTop: 4, opacity: 0.75 }}>
                    {fmtDateTime(n.created_at)}
                    {n.name ? ` · "${n.name}"` : ""}
                    {n.asset_type ? ` · ${n.asset_type}` : ""}
                    {n.use_stage ? ` · ${n.use_stage}` : ""}
                  </div>
                  {n.headline && (
                    <div style={{ marginTop: "0.55rem", color: "var(--midnight)", fontSize: "0.95rem", lineHeight: 1.5 }}>
                      {n.headline}
                    </div>
                  )}
                </>
              }
              right={
                <button type="button" onClick={() => setOpen((s) => ({ ...s, [n.id]: !s[n.id] }))} className="btn-outline-navy" data-testid={`admin-name-toggle-${n.id}`} style={{ padding: "0.4rem 0.7rem", fontSize: "0.8rem" }}>
                  {isOpen ? "Hide ▲" : "View ▼"}
                </button>
              }
            />
            {isOpen && (
              <div style={{ marginTop: "1rem", borderTop: "1px solid var(--hairline)", paddingTop: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>Their inputs</div>
                  <KV pairs={n.answers} />
                </div>
                <div>
                  <div className="eyebrow" style={{ marginBottom: "0.4rem" }}>Full report</div>
                  <PrettyJson data={n.report} />
                </div>
              </div>
            )}
          </Row>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// CHATS TAB
// ────────────────────────────────────────────────────────────

const ChatsTab = ({ token }) => {
  const { data, error, loading } = useAdminFetch("/admin/chats", token);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState({});
  const chats = data?.chats || [];

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return chats;
    return chats.filter((c) =>
      `${c.first_name} ${c.email}`.toLowerCase().includes(f) ||
      (c.last_message_preview || "").toLowerCase().includes(f)
    );
  }, [chats, filter]);

  const totalUserMessages = chats.reduce((acc, c) => acc + (c.user_message_count || 0), 0);

  return (
    <div data-testid="admin-chats-tab">
      <FilterBar filter={filter} setFilter={setFilter} testId="admin-chats-filter">
        <Stat label="Conversations" value={chats.length} testId="admin-chats-stat-convos" />
        <Stat label="User messages" value={totalUserMessages} testId="admin-chats-stat-msgs" />
      </FilterBar>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {!loading && !error && visible.length === 0 && (
        <p style={{ color: "var(--cobalt)" }}>No chats yet. Once users start asking Joey questions, they'll appear here.</p>
      )}

      {visible.map((c) => {
        const isOpen = !!open[c.user_id];
        return (
          <Row key={c.user_id} testId={`admin-chat-${c.user_id}`}>
            <Header
              left={
                <>
                  <Identity first_name={c.first_name} email={c.email} />
                  <div style={{ fontSize: "0.82rem", color: "var(--midnight)", marginTop: 4, opacity: 0.75 }}>
                    {c.user_message_count} user message{c.user_message_count === 1 ? "" : "s"} · Last activity {fmtDateTime(c.last_message_at)}
                  </div>
                  {!isOpen && c.last_message_preview && (
                    <div style={{ marginTop: "0.6rem", fontSize: "0.9rem", color: "var(--midnight)", opacity: 0.85, fontStyle: "italic" }}>
                      {c.last_message_role === "assistant" ? "Joey: " : `${c.first_name}: `}
                      {c.last_message_preview}{(c.last_message_preview || "").length >= 200 ? "..." : ""}
                    </div>
                  )}
                </>
              }
              right={
                <button type="button" onClick={() => setOpen((s) => ({ ...s, [c.user_id]: !s[c.user_id] }))} className="btn-outline-navy" data-testid={`admin-chat-toggle-${c.user_id}`} style={{ padding: "0.4rem 0.7rem", fontSize: "0.8rem" }}>
                  {isOpen ? "Hide ▲" : "Open ▼"}
                </button>
              }
            />
            {isOpen && (
              <div style={{ marginTop: "1rem", borderTop: "1px solid var(--hairline)", paddingTop: "1rem", display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                {(c.messages || []).map((m, i) => (
                  <div key={`${c.user_id}-${m.ts || i}`} style={{ display: "flex", flexDirection: m.role === "user" ? "row-reverse" : "row", gap: "0.7rem" }}>
                    <div style={{
                      background: m.role === "user" ? "var(--navy)" : "#ffffff",
                      color: m.role === "user" ? "var(--warm-white)" : "var(--midnight)",
                      border: m.role === "user" ? "1px solid var(--navy)" : "1px solid var(--hairline)",
                      padding: "0.7rem 0.9rem",
                      borderRadius: 3,
                      maxWidth: "85%",
                      whiteSpace: "pre-wrap",
                      fontSize: "0.92rem",
                      lineHeight: 1.5,
                    }}>
                      <div style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4, opacity: 0.75 }}>
                        {m.role === "user" ? c.first_name : "Joey (AI)"} · {fmtDateTime(m.ts)}
                      </div>
                      <div>{m.role === "user" ? m.content : renderInline(m.content)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Row>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Tiny helpers: key-value display + pretty JSON
// ────────────────────────────────────────────────────────────

const KV = ({ pairs }) => {
  if (!pairs || typeof pairs !== "object") return null;
  const entries = Object.entries(pairs);
  if (!entries.length) return <p style={{ color: "var(--cobalt)" }}>—</p>;
  return (
    <div style={{ fontSize: "0.88rem", lineHeight: 1.5 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.4rem", padding: "0.4rem 0", borderBottom: "1px dashed var(--hairline)" }}>
          <strong style={{ minWidth: 140, color: "var(--cobalt)", textTransform: "uppercase", fontSize: "0.72rem", letterSpacing: "0.05em" }}>{k}</strong>
          <span style={{ color: "var(--midnight)", flex: 1 }}>
            {Array.isArray(v) ? v.join(", ") || "—" : (v ?? "—")}
          </span>
        </div>
      ))}
    </div>
  );
};

const PrettyJson = ({ data }) => {
  if (!data) return <p style={{ color: "var(--cobalt)" }}>—</p>;
  return (
    <pre style={{
      fontSize: "0.78rem",
      lineHeight: 1.5,
      background: "#fff",
      border: "1px solid var(--hairline)",
      padding: "0.8rem 1rem",
      maxHeight: 500,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};
